import { useEffect } from 'react';
import { getAnalyticsItemType } from '@mb/lib';
import { useIntl } from 'react-intl';

import { getAnalyticsItemCategoriesByTaxonomy } from '../helpers/analytics';
import controlRulesById from '../RecommendationsModalContainer/controlRulesById';
import {
  ModalProps,
  RecommendedItemType,
} from '../types/recommendationsModal.types';
import { pushGtmDataLayer } from '../utils';

export const BANNER_SUBTITLE_DEFAULT_TEXT =
  'This sample is not currently in stock, it will ship directly from the brand.';

export function useRecommendationsModalAnalytics(
  isOpen: boolean,
  modalProps: ModalProps,
  proposal: Record<string, any> | null,
  isViewEventTriggered: boolean,
  setIsViewEventTriggered: (value: boolean) => void,
) {
  const intl = useIntl();

  useEffect(() => {
    const triggerGaEvent = (
      items: RecommendedItemType[],
      isServerMessage: boolean = false,
      controlRuleId: string | number | null | undefined,
      isServiceModal: boolean = false,
    ) => {
      if (isViewEventTriggered) {
        return;
      }

      pushGtmDataLayer({ ecommerce: null });

      if (isServiceModal) {
        pushGtmDataLayer({
          event: 'view_item_list',
          event_location: 'Catalog',
          list_id: 'sm_diverzify',
          list_name: 'Diverzify',
          ecommerce: {
            item_list_id: 'sm_diverzify', // variable based on where the list was viewed
            item_list_name: 'Diverzify', // readable list name, variable based on where the list was viewed
            items: [
              {
                item_id: proposal?.productSku || null,
                item_name: 'Flooring Install', // TODO remove hardcode
                item_brand: 'Diverzify', // TODO remove hardcode
                item_variant: null, // from product data, not filters
                item_list_id: 'sm_diverzify', // variable based on where the list was viewed
                item_list_name: 'Diverzify', // readable list name, variable based on where the list was viewed
                index: 1,
                quantity: 1,
                price: 0, // no currency code or dollar sign
                item_category: null,
                item_category2: null,
                item_category3: null,
              },
            ],
          },
        });
      } else {
        const controlRule =
          controlRuleId != null
            ? controlRulesById[controlRuleId ?? '']
            : {
                list_id_suffix: 'system_rule',
                list_name_prefix: 'System rule',
              };
        const controlRuleListId =
          controlRule?.list_id_suffix != null
            ? `sim_${controlRule?.list_id_suffix}`
            : 'sim_new_control_rule';
        const controlRuleListName =
          controlRule?.list_name_prefix != null
            ? `${controlRule?.list_name_prefix} SIM`
            : 'New Control Rule SIM';
        const controlRuleItemListId =
          controlRule?.list_id_suffix != null
            ? `sim_${controlRule?.list_id_suffix}`
            : 'sim_new_control_rule';
        const controlRuleItemListName =
          controlRule?.list_name_prefix != null
            ? `${controlRule?.list_name_prefix} SIM`
            : 'New Control Rule SIM';
        const listId = isServerMessage ? controlRuleListId : 'sim_oos';
        const listName = isServerMessage
          ? controlRuleListName
          : 'Out of stock SIM';
        const itemListId = isServerMessage ? controlRuleItemListId : 'sim_oos';
        const itemListName = isServerMessage
          ? controlRuleItemListName
          : 'Out of stock SIM';

        const dataLayerRecommendationItems = items?.map(
          (item: RecommendedItemType, index: number) => {
            const formattedRecommendedItem = {
              item_id: item?.originalProduct?.sku,
              item_name: item?.originalProduct?.name,
              item_brand: item?.originalProduct?.manufacturer,
              item_type: getAnalyticsItemType(item?.originalProduct),
              item_list_id: itemListId,
              item_list_name: itemListName,
              index: index + 1,
              quantity: 1,
              price: 0,
              item_category4:
                item?.originalProduct?.industry_sector?.join(', ') || '',
              item_category5: modalProps.bagProject?.project_type_label || '',
            };
            return {
              ...formattedRecommendedItem,
              ...getAnalyticsItemCategoriesByTaxonomy(
                item?.originalProduct?.taxonomy,
              ),
            };
          },
        );

        pushGtmDataLayer({
          event: 'view_item_list',
          list_id: listId,
          list_name: listName,
          ecommerce: {
            item_list_id: itemListId,
            item_list_name: itemListName,
            items: dataLayerRecommendationItems,
          },
        });
      }

      setIsViewEventTriggered(true);
    };

    if (
      isOpen &&
      modalProps.isInitMaterialAddedToBag &&
      (modalProps.recommendedItems?.length > 0 || proposal != null)
    ) {
      const isServerMessage =
        modalProps?.bannerShippingSubtitle !== BANNER_SUBTITLE_DEFAULT_TEXT;
      const controlRuleId = modalProps?.controlRuleId;
      const isServiceModal = proposal != null;

      triggerGaEvent(
        modalProps?.recommendedItems,
        isServerMessage,
        controlRuleId,
        isServiceModal,
      );
    }
  }, [modalProps, proposal, intl, isViewEventTriggered]);
}
